import { useState } from 'react';
import { BadgeCheck, Box, Star, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatMarketplacePrice } from '../../lib/marketplace';
import { trackMarketplaceEvent } from '../../lib/marketplaceAnalytics';
import type { MarketplaceListing } from '../../types/marketplace';
import { ListingOriginBadge } from './ListingOriginBadge';

const typeLabels: Record<MarketplaceListing['type'], string> = {
  'digital-product': 'Digital',
  service: 'Service',
  course: 'Course',
  community: 'Community',
};

const categoryBadgeGradients: Record<MarketplaceListing['type'], string> = {
  'digital-product': 'from-blue-500/20 to-indigo-500/20 text-blue-600 dark:text-blue-300 border-blue-500/20',
  service: 'from-purple-500/20 to-pink-500/20 text-purple-600 dark:text-purple-300 border-purple-500/20',
  course: 'from-amber-500/20 to-orange-500/20 text-amber-600 dark:text-amber-300 border-amber-500/20',
  community: 'from-emerald-500/20 to-teal-500/20 text-emerald-600 dark:text-emerald-300 border-emerald-500/20',
};

export const MarketplaceListingCard = ({
  listing,
  eagerImage = false,
}: {
  listing: MarketplaceListing;
  eagerImage?: boolean;
}) => {
  const [imageError, setImageError] = useState(false);

  const sellerProfileHref = listing.seller.username
    ? `/@${encodeURIComponent(listing.seller.username)}`
    : null;
  const sellerName = listing.seller.name
    || listing.seller.username
    || 'Wersee creator';

  const priceText = formatMarketplacePrice(listing);
  const showFallbackImage = !listing.imageUrl || imageError;

  return (
    <article className="group relative flex h-full min-w-0 flex-col rounded-[26px] border border-black/[0.07] bg-white/70 p-3 shadow-[0_4px_20px_rgba(0,0,0,0.03)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-black/15 hover:shadow-[0_18px_45px_rgba(0,0,0,0.08)] dark:border-white/[0.08] dark:bg-[#111113]/80 dark:shadow-[0_4px_20px_rgba(0,0,0,0.4)] dark:hover:border-white/20 dark:hover:shadow-[0_18px_45px_rgba(0,0,0,0.65)]">
      <Link
        to={listing.href}
        aria-label={`Open ${listing.title}`}
        onClick={() =>
          trackMarketplaceEvent('marketplace_listing_opened', {
            listing_id: listing.id,
            listing_type: listing.type,
          })
        }
        className="absolute inset-0 z-[1] rounded-[26px] outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      />

      {/* Image Container with Apple Pills */}
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[20px] bg-[#f0f0ed] dark:bg-[#18181a]">
        {!showFallbackImage ? (
          <img
            src={listing.imageUrl!}
            alt={`${listing.title} preview`}
            loading={eagerImage ? 'eager' : 'lazy'}
            decoding="async"
            onError={() => setImageError(true)}
            className="h-full w-full object-cover transition-transform duration-500 will-change-transform group-hover:scale-105"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-indigo-500/10 via-purple-500/5 to-pink-500/10 p-6 text-center">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/80 shadow-sm backdrop-blur-md dark:bg-white/10">
              <Box className="h-6 w-6 text-neutral-600 dark:text-white/70" />
            </div>
            <p className="mt-3 line-clamp-2 text-sm font-semibold text-neutral-700 dark:text-white/80">
              {listing.title}
            </p>
          </div>
        )}

        {/* Top-Left Category Pill & Origin Badge */}
        <div className="pointer-events-none absolute left-3 top-3 z-10 flex flex-wrap items-center gap-1.5 max-w-[calc(100%-85px)]">
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold backdrop-blur-md ${
              categoryBadgeGradients[listing.type] || 'border-black/10 bg-black/50 text-white'
            }`}
          >
            {typeLabels[listing.type]}
          </span>
          <ListingOriginBadge listing={listing} size="xs" />
        </div>

        {/* Top-Right Price Pill */}
        <div className="pointer-events-none absolute right-3 top-3 z-10">
          <span className="inline-flex items-center rounded-full border border-black/5 bg-white/95 px-3 py-1 text-xs font-bold text-neutral-950 shadow-md backdrop-blur-md dark:border-white/15 dark:bg-black/90 dark:text-white">
            {priceText}
          </span>
        </div>
      </div>

      {/* Card Content */}
      <div className="flex flex-1 flex-col p-2 pt-3.5">
        {/* Creator Info Pill */}
        <div className="flex items-center justify-between gap-2">
          {sellerProfileHref ? (
            <Link
              to={sellerProfileHref}
              aria-label={`View ${sellerName} profile`}
              onClick={() =>
                trackMarketplaceEvent('marketplace_seller_profile_opened', {
                  listing_id: listing.id,
                  seller_id: listing.seller.id,
                  seller_username: listing.seller.username,
                })
              }
              className="relative z-10 inline-flex min-w-0 max-w-[80%] items-center gap-1.5 rounded-full bg-black/[0.035] py-1 pl-1.5 pr-2.5 text-xs font-medium text-neutral-700 transition hover:bg-black/[0.07] hover:text-neutral-950 dark:bg-white/[0.06] dark:text-white/70 dark:hover:bg-white/[0.12] dark:hover:text-white"
            >
              {listing.seller.avatarUrl ? (
                <img
                  src={listing.seller.avatarUrl}
                  alt=""
                  className="h-4 w-4 rounded-full object-cover"
                />
              ) : (
                <span className="grid h-4 w-4 place-items-center rounded-full bg-indigo-500/20 text-[9px] font-bold text-indigo-700 dark:text-indigo-300">
                  {sellerName.slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="truncate">{sellerName}</span>
              {listing.seller.verification ? (
                <BadgeCheck
                  className="h-3.5 w-3.5 shrink-0 text-blue-500"
                  aria-label={listing.seller.verification.label}
                />
              ) : null}
            </Link>
          ) : (
            <span className="text-xs text-neutral-500 dark:text-white/50">{sellerName}</span>
          )}

          {listing.ratingCount > 0 && listing.ratingAverage !== null ? (
            <div className="flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
              <Star className="h-3 w-3 fill-current" />
              <span>{listing.ratingAverage.toFixed(1)}</span>
            </div>
          ) : (
            <span className="rounded-full bg-black/[0.04] px-2 py-0.5 text-[10px] font-medium text-neutral-500 dark:bg-white/[0.06] dark:text-white/40">
              New
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="mt-2.5 line-clamp-1 text-[15px] font-semibold tracking-[-0.02em] text-neutral-950 group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400">
          {listing.title}
        </h3>

        {/* Description */}
        {(listing.shortDescription || listing.description) ? (
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-neutral-500 dark:text-white/50">
            {listing.shortDescription || listing.description}
          </p>
        ) : null}

        {/* Bottom Action Pill */}
        <div className="mt-auto flex items-center justify-between pt-3 text-xs font-semibold">
          <span className="text-neutral-400 dark:text-white/35">
            {listing.category || 'Curated Offer'}
          </span>
          <span className="inline-flex items-center gap-1 text-neutral-900 group-hover:translate-x-0.5 transition-transform dark:text-white">
            Details <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </article>
  );
};
